<template>
  <div id="ManagementPanel" :class="{ active: view, close: !view }">
    <div class="mdl-grid">
      <div
        class="mdl-cell mdl-cell--1-col mdl-cell--12-col-tablet mdl-cell--12-col-phone"
      >
        <span class="material-icons closePanel" @click="close">close</span>
      </div>
      <div
        class="mdl-cell mdl-cell--11-col mdl-cell--12-col-tablet mdl-cell--12-col-phone"
      >
        <h3>{{ $t("creditNotifications.title") }}</h3>
        <p class="subTitle" v-html="$t('creditNotifications.bodyHtml')"></p>

        <div class="half-space"></div>

        <div class="mdl-grid noMargin">
          <div class="mdl-cell mdl-cell--12-col">
            <div class="cdl-input">
              <input
                type="text"
                name="emails"
                :disabled="!editMode"
                v-model="emailsText"
                @input="label($event)"
                :class="{
                  'not-empty': !!emailsText,
                  'has-success': saveSuccess,
                }"
              />
              <label>{{ $t("creditNotifications.emailLabel") }}</label>
              <span class="error" v-if="emailError">{{ emailError }}</span>
              <!-- Green checkmark aligned to the right of the input -->
              <span
                v-if="saveSuccess"
                class="material-icons successIcon"
                aria-label="saved"
              >
                check_circle
              </span>
            </div>
            <!-- Success message under input -->
            <p v-if="saveSuccess" class="successMsg">
              {{ $t("creditNotifications.savedInlineMsg") }}
            </p>
            <div
              class="contentButton"
              style="
                display: flex;
                gap: 8px;
                align-items: center;
                margin-top: 48px;
              "
            >
              <button v-if="editMode" class="cdlButton saveWide" @click="save">
                {{ $t("creditNotifications.save") }}
              </button>
              <button v-if="!editMode" class="cdlButton" @click="enableEdit">
                {{ $t("creditNotifications.edit") }}
              </button>
            </div>
            <!--            <p class="subSectionTwo" style="margin-top: 8px">-->
            <!--              <i>{{ $t('creditNotifications.multiHint') }}</i>-->
            <!--            </p>-->
          </div>
        </div>
      </div>
    </div>
    <StatusAlert v-if="viewError" :status="messageError === $t('creditNotifications.fixErrors') ? 'warning' : 'error'" role="alert">
      <template #body>
        <p>{{ messageError }}</p>
      </template>
    </StatusAlert>
  </div>
</template>

<script>
import "@/assets/scss/ManagementPanel.scss";
import StatusAlert from "@/components/Primitives/Alerts/StatusAlert.vue";
import { GestioneCreditoService } from "@/services/GestioneCreditoService";

export default {
  name: "CreditNotificationsPanel",
  components: { StatusAlert },
  props: {
    view: { type: Boolean, required: true },
    settings: {
      type: Object,
      required: false,
      default: () => ({ emails: [] }),
    },
  },
  data() {
    return {
      emailsText: "",
      editMode: true,
      viewError: false,
      messageError: "",
      emailError: "",
      saveSuccess: false,
      suppressNextSaveSuccessReset: false,
    };
  },
  methods: {
    close() {
      this.$emit("close-panel", { whichPanel: "creditNotifications" });
    },
    label(ev) {
      this.$LabelInput && this.$LabelInput(ev);
    },
    parseEmails(text) {
      return text
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    },
    validate() {
      const emails = this.parseEmails(this.emailsText);
      const emailRegex = /^\S+@\S+\.[\w-]{2,}$/;
      for (const e of emails) {
        if (!emailRegex.test(e)) {
          this.emailError = this.$t("creditNotifications.invalidEmail");
          return null;
        }
      }
      this.emailError = "";
      return emails;
    },
    async save() {
      const emails = this.validate();
      if (!emails) {
        this.viewError = true;
        this.messageError = this.$t("creditNotifications.fixErrors");
        setTimeout(() => (this.viewError = false), 4000);
        return;
      }
      try {
        await GestioneCreditoService.saveCreditNotifications({ emails });
        this.$emit("saved", { emails: [...emails] });
        // Keep panel open; switch to view mode showing saved emails
        this.emailsText = emails.join(", ");
        this.editMode = false;
        // Show inline success instead of toast
        this.saveSuccess = true;
        this.suppressNextSaveSuccessReset = true;
        this.viewError = false;
        this.messageError = "";
      } catch (e) {
        this.viewError = true;
        this.messageError = e?.message || "Save failed";
        setTimeout(() => (this.viewError = false), 4000);
      }
    },
    enableEdit() {
      this.editMode = true;
      this.saveSuccess = false;
      // place cursor at end optionally (browser default is fine)
    },
  },
  watch: {
    settings: {
      immediate: true,
      deep: true,
      handler(v) {
        const list = v && Array.isArray(v.emails) ? v.emails : [];
        this.emailsText = list.join(", ");
        // Start in edit mode only if there are no saved emails
        this.editMode = list.length === 0;
        // Do not show success message/icon when loading settings unless suppressed after save
        if (!this.suppressNextSaveSuccessReset) {
          this.saveSuccess = false;
        }
        // Always clear suppression flag after handling
        this.suppressNextSaveSuccessReset = false;
      },
    },
  },
};
</script>

<style scoped>
#ManagementPanel {
  min-height: 100vh;
}
#ManagementPanel .mdl-grid {
  min-height: 100vh;
}
/* Add more vertical spacing between title/body and content */
#ManagementPanel h3 {
  margin-bottom: 3rem;
}
#ManagementPanel .subTitle {
  margin-top: 6px;
  margin-bottom: 6px; /* halved spacing before input */
}
/* Position context for input container */
.cdl-input {
  position: relative;
}
/* Inline success styles */
.successMsg {
  color: #2e7d32;
  margin-top: 6px;
  font-size: 0.95rem;
}
/* Success icon to the right of the input */
.successIcon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #2e7d32;
  pointer-events: none;
}
/* Add right padding when success icon visible to avoid text overlap */
.cdl-input input.has-success {
  padding-right: 32px;
}
/* Make the Save button wider for better prominence */
.saveWide {
  min-width: 220px;
  padding-left: 28px;
  padding-right: 28px;
}
</style>
